/**
 * @fileoverview Product List component for displaying soil mix products
 * @module components/ProductList
 * @requires react
 * @requires react-router-dom
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import './ProductList.css';

/**
 * ProductList component for displaying soil mix products
 * @component
 * @returns {JSX.Element} ProductList component
 */
const ProductList = () => {
  // State for products
  const [products, setProducts] = useState([]);
  // State for loading status
  const [loading, setLoading] = useState(true);
  // State for error message
  const [error, setError] = useState(null);

  // Modal: Request Soil
  const [showRequestModal, setShowRequestModal] = useState(false);

  const openRequestModal = () => {
    setShowRequestModal(true);
  };

  const closeRequestModal = () => setShowRequestModal(false);

  /**
   * Fetch products from API
   */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        console.log('Fetching products from API...');
        
        const response = await api.get('/api/plants/search');
        console.log('Products API Response:', response);
        
        if (response.data.plants && Array.isArray(response.data.plants)) {
          console.log(`Found ${response.data.plants.length} products`);
          setProducts(response.data.plants);
        } else {
          console.error('Response data is not an array:', response.data);
          setError('Invalid response format from API');
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        
        // Log more details about the error
        if (err.response) {
          console.error('Error response status:', err.response.status);
          console.error('Error response data:', err.response.data);
        }
        
        setError(`Error fetching products: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  /**
   * Retry fetching products
   */
  const handleRetry = () => {
    setLoading(true);
    setError(null);
    
    // Fetch products again
    api.get('/api/plants/search')
    .then(response => {
        setProducts(response.data.plants);
      })
      .catch(err => {
        console.error('Error fetching products:', err);
        setError('Error fetching products. Please try again later.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="product-list-container">
      <h2 className="product-list-title">Our Soil Mixes</h2>
          {/* Request Soil Mix Banner */}
        <div className="request-mix-banner">
          <p>
            Can’t find your plant?{' '}
              <button
                type="button"
                onClick={openRequestModal}
                className="request-link request-link-button"
                aria-haspopup="dialog"
                aria-controls="request-soil-modal"
              >
                Request a soil mix here!
            </button>
          </p>
        </div>

        {showRequestModal && (
          <div
            id="request-soil-modal"
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Request a custom soil mix"
            onClick={closeRequestModal}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()} // keep clicks inside from closing
            >
              <button
                className="modal-close"
                onClick={closeRequestModal}
                aria-label="Close request form"
              >
                &times;
              </button>

              <h3>Request a Custom Soil Mix</h3>

              <form
                className="request-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                
                  const form = e.currentTarget;
                  const payload = {
                    name: form.name.value,
                    email: form.email.value,
                    plant: form.plant.value,
                    notes: form.notes.value,
                    company: form.company?.value || '' // honeypot (hidden input)
                  };
                
                  try {
                    const res = await api.post('/api/requests/soil', payload);
                    if (!res.data?.success) throw new Error('Failed to save request');
                
                    // UX: show success, reset, close
                    alert('Thanks for your request! We’ll get back to you soon.');
                    form.reset();
                    closeRequestModal();
                  } catch (err) {
                    console.error('Submit error:', err);
                    alert('Sorry, something went wrong. Please try again later.');
                  }
                }}
                noValidate
              >
                {/* Honeypot to discourage simple bots */}
                <input
                  type="text"
                  name="company"
                  autoComplete="off"
                  tabIndex="-1"
                  style={{ display: 'none' }}
                  aria-hidden="true"
                />

                <input type="text" name="name" placeholder="Your name" required />
                <input type="email" name="email" placeholder="Your email" required />
                <input type="text" name="plant" placeholder="Plant name" required />
                <textarea name="notes" placeholder="Notes (optional)" rows={4} />

                <button type="submit">Submit Request</button>
              </form>
            </div>
          </div>
        )}

      
      {loading ? (
        <div className="loading-message">Loading soil mixes...</div>
      ) : error ? (
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button className="retry-button" onClick={handleRetry}>
            Try Again
          </button>
          <div className="troubleshooting-tips">
            <h3>Troubleshooting Tips:</h3>
            <ul>
              <li>Check if the backend server is running</li>
              <li>Verify that your database is connected</li>
              <li>Check the server console for error messages</li>
              <li>Ensure the API endpoint is correctly implemented</li>
            </ul>
          </div>
        </div>
      ) : products.length > 0 ? (
        <div className="products-grid">
          {products.map(product => (
            <Link 
              to={`/products/${product._id}`} 
              className="product-card" 
              key={product._id}
            >
              <div className="product-image">
                <img 
                  src={product.soilMix.image || '/images/placeholder-soil.jpg'}
                  alt={`${product?.name || 'Plant'} Soil Mix`}
                />
              </div>
              <div className="product-info">
                <h3 className="product-name">{product.name} Mix</h3>
                <p className="product-price">${product.soilMix.price.toFixed(2)}</p>
                <div className="product-description">{product.soilMix.name}</div>
                <div className="view-details">View Details</div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="no-products">
          <p>No soil mixes found.</p>
        </div>
      )}
    </div>
  );
};

export default ProductList;